import { useCallback, memo } from "react";
import { FlatList, StyleSheet, Pressable, Text, Linking } from "react-native";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import AppStore from "../storage/appStore";
import { logError, FontAwesomeIcon, tabIconSize, os } from "../platform";
import { COLORS } from "../theme/colors";
import { STRINGS } from "../constants/strings";

// Custom comparison function for LinkItem
const arePropsEqual = (prevProps, nextProps) => {
  return (
    (prevProps.item.id || prevProps.item.url) === (nextProps.item.id || nextProps.item.url) &&
    prevProps.item.name === nextProps.item.name &&
    prevProps.isLast === nextProps.isLast
  );
};

// Memoized list item component with custom comparison
const LinkItem = memo(
  ({ item, isLast, onPress }) => (
    <Pressable
      style={[styles.card, isLast && styles.lastCard]}
      onPress={onPress}
      accessible={true}
      accessibilityRole="button"
      accessibilityLabel={item.name}
    >
      <Text style={styles.title} maxFontSizeMultiplier={1.2}>
        {item.name}
      </Text>
      <FontAwesomeIcon icon={faChevronRight} size={tabIconSize} color={COLORS.tertiary} />
    </Pressable>
  ),
  arePropsEqual
);

LinkItem.displayName = "LinkItem";

const Links = ({ route, navigation }) => {
  const type = route.name === "PayBills" ? "payLinks" : "links";
  const links = AppStore((state) => state.links);
  const payLinks = AppStore((state) => state.payLinks);
  const setErrorMessage = AppStore((state) => state.setErrorMessage);
  const appData = type === "payLinks" ? payLinks : type === "links" ? links : [];

  const keyExtractor = useCallback((item, index) => {
    return item.id || item.url || `link-${index}`;
  }, []);

  const handleLinkPress = useCallback(
    async (item) => {
      const url = item.url;
      if (!url) return;
      try {
        if (os === "web") {
          await Linking.openURL(url);
        } else {
          navigation.navigate("WebViewScreen", { url: url, title: item.name });
        }
      } catch (error) {
        logError("link_open_failed", {
          message: error.message,
          stack: error.stack,
          url: url,
          name: item.name,
        });
        setErrorMessage(STRINGS.failedToOpenLink);
      }
    },
    [setErrorMessage, navigation]
  );

  const renderItem = useCallback(
    ({ item, index }) => (
      <LinkItem item={item} isLast={index === appData.length - 1} onPress={() => handleLinkPress(item)} />
    ),
    [appData.length, handleLinkPress]
  );

  // getItemLayout for fixed-height items (improves scrolling performance)
  const getItemLayout = useCallback((_data, index) => {
    const ITEM_HEIGHT = 52; // paddingVertical (15 * 2) + borderBottomWidth (2) + text height (~22)
    return {
      length: ITEM_HEIGHT,
      offset: ITEM_HEIGHT * index,
      index,
    };
  }, []);

  return (
    <FlatList
      testID="flat-list-view"
      contentContainerStyle={styles.container}
      data={appData}
      keyExtractor={keyExtractor}
      renderItem={renderItem}
      getItemLayout={getItemLayout}
      removeClippedSubviews={true}
      maxToRenderPerBatch={10}
      updateCellsBatchingPeriod={50}
      initialNumToRender={10}
      windowSize={5}
    />
  );
};

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    width: "100%",
    maxWidth: 750,
    alignSelf: "center",
    paddingTop: 20,
    paddingBottom: 50,
    backgroundColor: COLORS.background,
  },
  card: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderColor: COLORS.border,
    borderBottomWidth: 2,
    paddingVertical: 15,
    marginHorizontal: 20,
    gap: 10,
  },
  lastCard: {
    borderBottomWidth: 0,
  },
  title: {
    fontSize: 18,
    color: COLORS.text,
  },
});

export default Links;
